import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography } from '@mui/material';
import { observer } from 'mobx-react-lite';

import {
  ListWorkMaterialDto,
  ListWorkPackageDto,
  ListWorkServiceDto,
  ProcessProjectForChangeDto,
  ProcessListForChangeDto,
  CreatorRole,
} from '@codefluegel/zeta-change-typescript-client';
import { useTranslation } from 'react-i18next';

import MaterialDataGrid from '../Material/MaterialDataGrid';
import {
  useMaterialColumns,
  useServiceColumns,
} from '../../utils/tableColumns';
import WorkPackageTable from '../WorkPackage/WorkPackageTable';
import { WorkItemWithType } from '../../types/workPackages';

// Define the props for the TabPanel component
interface TabPanelProps {
  children: React.ReactNode;
  index: number;
  value: number;
}

type WorkTabProps = {
  workPackages: ListWorkPackageDto[];
  workServices: ListWorkServiceDto[];
  workMaterials: ListWorkMaterialDto[];
  department: ProcessProjectForChangeDto | undefined;
  isFlightIncluded: boolean;
  process: ProcessListForChangeDto;
  handleWorkPackageModal: (id: number) => void;
  handleServiceModal: (id?: number) => void;
  handleMaterialModal: (id?: number) => void;
  tabIndex: number;
};

// Custom component for the content of each tab
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

// Helper function for accessibility props
function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
}
const WorkTabPanel = observer((props: WorkTabProps) => {
  const {
    workPackages,
    workServices,
    workMaterials,
    handleWorkPackageModal,
    handleServiceModal,
    handleMaterialModal,
    tabIndex,
  } = props;
  const [value, setValue] = useState(tabIndex || 0);
  const { t } = useTranslation();
  const materialColumns = useMaterialColumns();
  const serviceColumns = useServiceColumns();

  const handleChange = (event: SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const typedWorkServices: WorkItemWithType[] = workServices
    .filter(s => s.creatorRole === CreatorRole.CoreTeamMember)
    .map(s => ({
      ...s,
      type: 'service',
    }));
  const typedWorkMaterials: WorkItemWithType[] = workMaterials
    .filter(s => s.creatorRole === CreatorRole.CoreTeamMember)
    .map(m => ({
      ...m,
      type: 'material',
    }));
  useEffect(() => {
    setValue(tabIndex || 0);
  }, [tabIndex]);

  return (
    <Box sx={{ marginLeft: 2 }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={value}
          onChange={handleChange}
          aria-label="basic tabs example"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="button">
                  {t('workPackage.workPackage')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)', // Light grey background
                    color: 'rgba(0, 0, 0, 0.6)', // Slightly darker grey for text
                  }}
                >
                  ({workPackages.length})
                </Typography>
              </Box>
            }
            {...a11yProps(0)}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="button">{t('service.service')}</Typography>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    color: 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  ({typedWorkServices.length})
                </Typography>
              </Box>
            }
            {...a11yProps(1)}
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="button">
                  {' '}
                  {t('material.material')}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: '12px',
                    backgroundColor: 'rgba(0, 0, 0, 0.08)',
                    color: 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  ({typedWorkMaterials.length})
                </Typography>
              </Box>
            }
            {...a11yProps(2)}
          />
        </Tabs>
      </Box>
      <TabPanel value={value} index={0}>
        <WorkPackageTable
          workPackages={workPackages}
          handleWorkPackageModal={(id: number) => handleWorkPackageModal(id)}
        />
      </TabPanel>
      <TabPanel value={value} index={1}>
        <MaterialDataGrid
          rows={typedWorkServices}
          columns={serviceColumns}
          isEditable={false}
          showType={false}
          handleServiceModal={handleServiceModal}
          handleMaterialModal={handleMaterialModal}
        />
      </TabPanel>
      <TabPanel value={value} index={2}>
        <MaterialDataGrid
          rows={typedWorkMaterials}
          columns={materialColumns}
          showType={false}
          isEditable={false}
          handleServiceModal={handleServiceModal}
          handleMaterialModal={handleMaterialModal}
        />
      </TabPanel>
    </Box>
  );
});

export default WorkTabPanel;
